import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { submitConsultation } from "../lib/api";
import { Loader2, Sparkles } from "lucide-react";

const SERVICES = [
  "AI & Automation",
  "Web Development",
  "Efficiency Systems",
  "UI/UX Design",
  "Other",
];

export const ConsultationDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setService = (s) => setForm((f) => ({ ...f, service: s }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitConsultation(form);
      toast.success("Consultation request received!", {
        description: "Our team will reach out within 24 hours.",
      });
      setForm({ name: "", email: "", company: "", service: "", message: "" });
      onOpenChange(false);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="consultation-dialog"
        className="bg-[#0A0510] border border-[#B026FF]/30 text-white max-w-lg overflow-hidden p-0"
      >
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#B026FF]/30 blur-3xl pointer-events-none" />
        <div className="relative p-7">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Sparkles size={20} className="text-[#B026FF]" />
              Get a Free Consultation
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Tell us about your project. We&apos;ll get back within 24 hours.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input
                data-testid="consult-name-input"
                placeholder="Full name *"
                value={form.name}
                onChange={update("name")}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-11 focus-visible:ring-[#B026FF]/50 focus-visible:border-[#B026FF]/40"
              />
              <Input
                data-testid="consult-email-input"
                type="email"
                placeholder="Email *"
                value={form.email}
                onChange={update("email")}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-11 focus-visible:ring-[#B026FF]/50 focus-visible:border-[#B026FF]/40"
              />
            </div>
            <Input
              data-testid="consult-company-input"
              placeholder="Company (optional)"
              value={form.company}
              onChange={update("company")}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 h-11 focus-visible:ring-[#B026FF]/50 focus-visible:border-[#B026FF]/40"
            />

            <div>
              <p className="text-xs uppercase tracking-widest text-white/50 mb-2">
                Interested in
              </p>
              <div className="flex flex-wrap gap-2">
                {SERVICES.map((s) => (
                  <button
                    type="button"
                    key={s}
                    data-testid={`consult-service-${s.toLowerCase().replace(/\s+/g, "-").replace("/", "-").replace("&", "and")}`}
                    onClick={() => setService(s)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${
                      form.service === s
                        ? "border-[#B026FF] bg-[#B026FF]/20 text-white"
                        : "border-white/15 text-white/65 hover:text-white hover:border-white/35"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <Textarea
              data-testid="consult-message-input"
              placeholder="Tell us about your project... *"
              value={form.message}
              onChange={update("message")}
              rows={4}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-[#B026FF]/50 focus-visible:border-[#B026FF]/40 resize-none"
            />

            <Button
              type="submit"
              data-testid="consult-submit-btn"
              disabled={submitting}
              className="btn-neon w-full h-12 rounded-full font-semibold border-0"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Request →"
              )}
            </Button>
            <p className="text-[11px] text-white/40 text-center">
              By submitting, you agree to be contacted by FlowBase regarding your enquiry.
            </p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
